import React, { useEffect, useMemo, useState } from "react";
import useApp from "../../../utils/useApp";
import { Badge, Button, Col, Container, Dropdown, DropdownButton, Form, Row, Spinner } from "react-bootstrap";
import { fetchAllResources, fetchResourceCategories, postResourceCategory } from "../../../services/httpClient";
import { Resource } from "../../../types/resource";
import { unsavedChanges } from "../../../App";
import SaveChangesModal from "../../ui/SaveChangesModal";
import { faClose, faFilter } from "@fortawesome/free-solid-svg-icons";
import OrgResourceCard from "./OrgResourceCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "react-tooltip";
import AddResource from "./AddResource";
import CategoryCard from "./CategoryCard";
import Input from "../../ui/Input";

export const filterByCategory = <T extends { category_key?: string | null }>(resources: T[], categoryKey?: string | null): T[] => {
  if (!categoryKey) {
    return resources;
  }

  return resources.filter((r) => r.category_key === (categoryKey === "0" ? null : categoryKey));
};

interface OrgResourcesState {
  setOrgToSave: any;
  getData: boolean;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgResources = ({ setOrgToSave, getData, setShowToast, setToastText, setToastBody }: OrgResourcesState) => {
  const { auth, showMenu }: any = useApp();
  const [showAddResource, setShowAddResource] = useState<boolean>(false);
  const [showConsumables, setShowConsumables] = useState<boolean>(false);
  const [showNonConsumables, setShowNonConsumables] = useState<boolean>(false);
  const [showCategoryView, setShowCategoryView] = useState<boolean>(false);
  const [showAddCategory, setShowAddCategory] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [consumables, setConsumables] = useState<Resource[]>([]);
  const [nonConsumables, setNonConsumables] = useState<Resource[]>([]);
  const [resourceCategories, setResourceCategories] = useState<any[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>("consumable");
  const [disableType, setDisableType] = useState<boolean>(false);
  const [resourceType, setResourceType] = useState<string>("0");
  const [filter, setFilter] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState<string>("");

  const filteredConsumables = useMemo(() => filterByCategory(consumables, filter?.key), [consumables, filter]);
  const filteredNonConsumables = useMemo(() => filterByCategory(nonConsumables, filter?.key), [nonConsumables, filter]);

  useEffect(() => {
    if (getData) {
      getResources(true);
      getResourceCategories();
    }
  }, [getData]);

  useEffect(() => {
    getResources(true);
    getResourceCategories();
  }, [auth?.profile]);

  useEffect(() => {
    typeFilter === "all" || typeFilter === "consumable" ? setShowConsumables(true) : setShowConsumables(false);
    typeFilter === "all" || typeFilter === "nonconsumable" ? setShowNonConsumables(true) : setShowNonConsumables(false);
  }, [typeFilter]);

  const getResources = (resetFilter?: boolean) => {
    setIsLoading(true);
    fetchAllResources()
      .then((res: any) => {
        const resources = res[0].data;
        const consumables = resources.filter((r: any) => r.type_key === "consumable");
        const nonConsumables = resources.filter((r: any) => r.type_key === "nonconsumable");
        setConsumables(consumables);
        setNonConsumables(nonConsumables);
        if (resetFilter) {
          setFilter(null);
        }
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getResourceCategories = async (filterName?: string) => {
    await fetchResourceCategories()
      .then((res: any) => {
        let cats = res[0].data;
        cats = [{ id: 0, key: "0", name: "None" }, ...cats];
        setResourceCategories(cats);
        if (filterName) {
          const newFilter = cats.find((c: any) => c.name === filterName);
          setFilter(newFilter);
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickAddResourceCategory = (newCategoryName: string) => {
    return postResourceCategory({ name: newCategoryName })
      .then((res: any) => {
        if (showAddCategory) {
          setShowToast(true);
          setToastText("Category Created");
          setToastBody(newCategoryName);
          setNewCategoryName("");
          setShowAddCategory(false);
        }
        getResourceCategories(newCategoryName);
        return { key: newCategoryName, name: newCategoryName, id: res[0].data.category_id };
      })
      .catch((err: any) => {
        console.log(err);
        throw err;
      });
  };

  const onClickSaveSubmitNewCategory = (e: any) => {
    e?.preventDefault();
    onClickAddResourceCategory(newCategoryName);
  };

  const onClickAddCategoryOnly = () => {
    setShowAddCategory(true);
    setErrMsg("");
  };

  const onClickAddCategoryOnlyCancel = () => {
    setNewCategoryName("");
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onUpdateNewCategoryName = (e: any) => {
    setNewCategoryName(e.target.value);
    setErrMsg("");
  };

  const onClickViewCategories = () => {
    setShowCategoryView(true);
    setShowAddResource(false);
    setErrMsg("");
  };

  const onClickBack = () => {
    setShowCategoryView(false);
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onClickAddResource = (type: string) => {
    setErrMsg("");
    setShowAddResource(true);
    setDisableType(true);
    setResourceType(type);
  };

  const onClickAddResourceCancel = () => {
    unsavedChanges.value = false;
    setErrMsg("");
    setShowAddResource(false);
    setOrgToSave(false);
    getResources(true);
  };

  const onClickFilterType = (type: string) => {
    setTypeFilter(type);
    setShowAddResource(false);
  };

  const onClickFilterResource = (selectedFilter: any) => {
    setFilter(filter?.key === selectedFilter?.key ? null : selectedFilter);
  };

  const onClickClearFilters = () => {
    setFilter(null);
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : showCategoryView ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col className="col-5">
              <h4 className="app-header-text">Resource Categories</h4>
            </Col>
            <Col className="text-end mb-3">
              <Button className="app-primary-color button me-3" onClick={onClickAddCategoryOnly} disabled={showAddCategory}>
                Add Category
              </Button>
              <Button className="app-primary-color button" onClick={onClickBack}>
                Back
              </Button>
            </Col>
          </Row>
          {showAddCategory && (
            <Form onSubmit={(e: any) => onClickSaveSubmitNewCategory(e)}>
              <Row className="mb-3">
                <Col xs={6}>
                  <Input
                    label="Name"
                    type="text"
                    value={newCategoryName}
                    onUpdate={(e: any) => onUpdateNewCategoryName(e)}
                    hasFocus={true}
                  />
                </Col>
                <Col xs={"auto"} className="d-flex align-items-center">
                  <Button type="submit" className="app-secondary-color button me-3" disabled={!newCategoryName}>
                    Add
                  </Button>
                  <div className="vr my-2" />
                  <Button className="app-primary-color button ms-3" onClick={onClickAddCategoryOnlyCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
          <Row>
            <Col xs={6}>
              {resourceCategories?.slice(1).length === 0 ? (
                <p className="mt-2">No Categories</p>
              ) : (
                resourceCategories
                  .slice(1)
                  .map((category: any) => (
                    <CategoryCard
                      key={category.id}
                      category={category}
                      getResourceCategories={getResourceCategories}
                      getResources={getResources}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))
              )}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col xs={3}>
              <h4 className="app-header-text">Resources</h4>
            </Col>
            <Col className="text-end">
              {!showCategoryView && (
                <>
                  <Button className="app-secondary-color button me-3" onClick={onClickViewCategories}>
                    Categories
                  </Button>
                  <Button className="app-primary-color button" onClick={() => onClickAddResource(typeFilter)}>
                    Create Resource
                  </Button>
                </>
              )}
            </Col>
          </Row>
          <Row className={filter ? "mb-2" : "mb-4"}>
            <Col className="d-flex justify-content-center gap-3">
              <Button
                className={typeFilter === "consumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => onClickFilterType("consumable")}
              >
                Consumable
              </Button>
              <Button
                className={typeFilter === "nonconsumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => onClickFilterType("nonconsumable")}
              >
                Non-Consumable
              </Button>
              <div data-tooltip-id="tooltip-filter" data-tooltip-content={"Category Filter"}>
                <DropdownButton
                  id={filter ? "dropdown-filter-button-active" : "dropdown-filter-button"}
                  title={<FontAwesomeIcon icon={faFilter} />}
                >
                  {resourceCategories?.map((cat: any) => (
                    <Dropdown.Item
                      key={cat.id}
                      href={`#/action-1`}
                      className={filter?.key === cat.key ? "app-tertiary-color-inverse" : ""}
                      onClick={() => onClickFilterResource(cat)}
                    >
                      <text className="text-capitalize">{cat.name}</text>
                      {filter?.key === cat.key ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </div>
              <Tooltip id="tooltip-filter" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
            </Col>
          </Row>
          {filter && (
            <Row className="mb-3">
              <Col className="col-12 text-center">
                <Badge className="app-tertiary-color-inverse" bg="none">
                  {filter?.name}
                </Badge>
                <Badge className="app-tertiary-color ms-1 cursor-pointer" bg="none" onClick={onClickClearFilters}>
                  <FontAwesomeIcon icon={faClose}></FontAwesomeIcon>
                </Badge>
              </Col>
            </Row>
          )}
          <Row className="mb-3">
            <Col xs={12}>
              {showAddResource && (
                <AddResource
                  setOrgToSave={setOrgToSave}
                  disableType={disableType}
                  type={resourceType}
                  categoryFilter={filter}
                  setCategoryFilter={setFilter}
                  setShowAddResource={setShowAddResource}
                  getResources={getResources}
                  resourceCategories={resourceCategories}
                  getResourceCategories={getResourceCategories}
                  onAddResourceCategory={onClickAddResourceCategory}
                  setErrMsg={setErrMsg}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                />
              )}
              {typeFilter === "nonconsumable" && filteredNonConsumables.length === 0 ? (
                <p className="mt-2">No Non-Consumable Resources</p>
              ) : (
                typeFilter === "consumable" && filteredConsumables.length === 0 && <p className="mt-2">No Consumable Resources</p>
              )}
              {showConsumables && (
                <>
                  {filteredConsumables.map((resource: Resource, index: number) => (
                    <OrgResourceCard
                      key={index}
                      resource={resource}
                      getResources={getResources}
                      resourceCategories={resourceCategories}
                      onAddResourceCategory={onClickAddResourceCategory}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))}
                </>
              )}
              {showNonConsumables && (
                <>
                  {filteredNonConsumables.map((resource: Resource, index: number) => (
                    <OrgResourceCard
                      key={index}
                      resource={resource}
                      getResources={getResources}
                      resourceCategories={resourceCategories}
                      onAddResourceCategory={onClickAddResourceCategory}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))}
                </>
              )}
            </Col>
          </Row>
        </>
      )}
      <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onClickAddResourceCancel} />
    </>
  );
};

export default OrgResources;
